import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, Mail, MapPin, Phone } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <div className="mb-8">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/auth">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Sign In
            </Link>
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <FileText className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Privacy Policy</h1>
          </div>
          <p className="text-muted-foreground">Effective Date: January 1, 2026 | Last Updated: January 1, 2026</p>
        </div>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground leading-relaxed">
            This Privacy Policy explains how Entrepreneurs Across Borders (EAB) ("EAB," "we," "us," or "our") collects, uses, discloses, and protects information when you use EAB Connect (the "Platform"), available at eabconnect.lovable.app, and any related pages, forms, and services (collectively, the "Services").
          </p>
          <p className="text-muted-foreground leading-relaxed">
            This Policy is intended to provide clear notice consistent with Jamaica's data protection framework and common U.S. privacy disclosure requirements, including providing information about what we collect and why.
          </p>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">1. Who We Are</h2>
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <p><strong>Data Controller:</strong> Entrepreneurs Across Borders (EAB)</p>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span>info@eaborders.org</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>690 E. Main Street, Little Falls, NY 13365</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span>(315) 520-8748</span>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">2. Information We Collect</h2>
            <h3 className="text-lg font-medium text-foreground">A. Information you provide</h3>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li><strong>Account and profile information:</strong> name, email, phone (optional), location (country/parish), preferred language, LinkedIn URL (optional), biography, photo (optional)</li>
              <li><strong>Entrepreneurship and program information:</strong> business idea details, industry, stage, needs assessments, course responses, pitch-deck inputs, applications, survey responses</li>
              <li><strong>Mentorship and community information:</strong> mentor/mentee preferences, connection requests, messages you send through the Platform</li>
              <li><strong>Support and communications:</strong> emails, messages, feedback, and support requests</li>
            </ul>

            <h3 className="text-lg font-medium text-foreground">B. Information collected automatically</h3>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li><strong>Device and usage data:</strong> IP address, browser type, device identifiers, pages viewed, timestamps, referral URLs, and interactions with Platform features</li>
              <li><strong>Cookies and similar technologies:</strong> used to keep you logged in, remember preferences, and understand how the Platform is used</li>
            </ul>

            <h3 className="text-lg font-medium text-foreground">C. Information from partners</h3>
            <p className="text-muted-foreground">If you join through a partner organization (e.g., a school, nonprofit, program), we may receive limited enrollment or participant information from them.</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">3. How We Use Information</h2>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li><strong>Provide and operate the Platform:</strong> create accounts, authenticate users, maintain profiles, enable course access, facilitate mentor matching and community features</li>
              <li><strong>Program administration:</strong> track learning progress, completion, surveys, resource referrals, and program effectiveness</li>
              <li><strong>Communications:</strong> send service notices, respond to inquiries, deliver program updates, and share information you request</li>
              <li><strong>Safety, security, and fraud prevention:</strong> protect accounts, prevent abuse, and enforce our Terms</li>
              <li><strong>Analytics and improvements:</strong> understand usage trends and improve features and content</li>
              <li><strong>Legal and compliance:</strong> meet legal obligations and respond to lawful requests</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">4. Legal Bases for Processing</h2>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li><strong>Consent:</strong> when you choose to provide optional profile details or opt in to certain communications</li>
              <li><strong>Contract / service necessity:</strong> to provide the Services you request</li>
              <li><strong>Legitimate interests:</strong> operating a safe, effective platform; improving services; preventing fraud</li>
              <li><strong>Legal obligation:</strong> compliance and recordkeeping</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">5. How We Share Information</h2>
            <p className="text-muted-foreground">We do not sell your personal information. We may share information in these ways:</p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li><strong>Within the EAB Connect community:</strong> Depending on your account type and settings, certain profile fields may be visible to other authenticated users</li>
              <li><strong>Service providers:</strong> trusted vendors to host, maintain, and support the Platform</li>
              <li><strong>Partners you engage with:</strong> limited information necessary to administer programs you participate in</li>
              <li><strong>Legal, safety, and enforcement:</strong> to comply with law, protect rights and safety, investigate fraud or abuse</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">6. Sensitive Data</h2>
            <p className="text-muted-foreground">
              Some information you provide may be considered sensitive. Where required, we obtain appropriate consent or provide required choices for sensitive data processing. We ask that you do not submit highly sensitive personal information unless specifically requested and clearly necessary.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">7. Cookies and Similar Technologies</h2>
            <p className="text-muted-foreground">We may use cookies for:</p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Essential platform functions (login/session)</li>
              <li>Preferences (language, basic settings)</li>
              <li>Analytics (understanding usage to improve the Platform)</li>
            </ul>
            <p className="text-muted-foreground">You can control cookies through your browser settings.</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">8. Data Retention</h2>
            <p className="text-muted-foreground">
              We keep personal data only as long as reasonably necessary for the purposes described in this Policy, including program administration, legal compliance, dispute resolution, and enforcing agreements.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">9. Security</h2>
            <p className="text-muted-foreground">
              We use reasonable administrative, technical, and organizational safeguards designed to protect personal data. No system is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">10. Your Rights and Choices</h2>
            <p className="text-muted-foreground">You may request:</p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Access to the personal data we hold about you</li>
              <li>Correction of inaccurate or incomplete data</li>
              <li>Deletion of data (where appropriate)</li>
              <li>Information about our processing and disclosures</li>
              <li>A portable copy of your data</li>
            </ul>
            <p className="text-muted-foreground">
              Submit requests by contacting: <a href="mailto:info@eaborders.org" className="text-primary hover:underline">info@eaborders.org</a> with the subject line "Privacy Request".
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">11. California Notice (CCPA/CPRA)</h2>
            <p className="text-muted-foreground">Categories of personal information we may collect:</p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Identifiers (name, email, IP address)</li>
              <li>Customer records / account data (profile details, preferences)</li>
              <li>Commercial/program information (course progress, surveys, program participation)</li>
              <li>Internet or network activity (usage data, interactions)</li>
              <li>Approximate location (country/parish; IP-based location)</li>
              <li>Professional information (LinkedIn URL or work history, if provided)</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">12. Children and Student Users</h2>
            <p className="text-muted-foreground">
              The Platform is not intended for children under 13. If you believe a child under 13 has provided personal information, contact us and we will take appropriate steps to remove it.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">13. International Transfers</h2>
            <p className="text-muted-foreground">
              EAB may process personal data in the United States and other countries where our service providers operate. Where required, we will use appropriate safeguards for cross-border transfers.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">14. Third-Party Links</h2>
            <p className="text-muted-foreground">
              The Platform may contain links to third-party websites or services. We are not responsible for the privacy practices of those third parties.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">15. Changes to This Policy</h2>
            <p className="text-muted-foreground">
              We may update this Policy from time to time. We will update the "Last Updated" date and, where appropriate, provide additional notice within the Platform. Continued use after changes means you accept the updated Policy.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">16. Contact Us</h2>
            <p className="text-muted-foreground">For privacy questions or requests, contact:</p>
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <a href="mailto:info@eaborders.org" className="text-primary hover:underline">info@eaborders.org</a>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>690 E. Main Street, Little Falls, NY 13365</span>
              </div>
            </div>
          </section>

          <div className="border-t pt-6 mt-8">
            <p className="text-sm text-muted-foreground text-center">
              You can also download the full privacy policy as a PDF: {" "}
              <a 
                href="/EAB_Connect_Privacy_Policy.pdf" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Download PDF
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
